/**
 * Document-wide emitted-handle bookkeeping for the export orchestrator.
 *
 * Extracted out of `dxfBridge.export.ts` (AW-0, docs/MASTERPLAN.md AW-0) to
 * keep that orchestrator under the CLAUDE.md 2000-line guardrail — a pure
 * move, no behaviour change. These three helpers have no dependency back on
 * `dxfBridge.export.ts` (they operate only on the assembled `DxfSection[]`),
 * so the split introduces no import cycle.
 */
import {
  formatHexHandle,
  maxHexHandleValueBig,
  type DxfSection,
} from "@dxf-ai-editor/dxf-io";

import { collectHandlesDeep } from "./dxfBridge.annotativeExport";

/**
 * Build the document-wide set of already-used handles (lowercased) that the
 * synthetic MLEADER STYLE-handle minter must avoid. Seeds from every
 * assembled section so the mint cannot collide with an entity, block,
 * table, OBJECTS, layout, or dynamic-block handle.
 */
export function collectAllEmittedHandles(sections: DxfSection[]): Set<string> {
  const used = new Set<string>();
  for (const section of sections) collectHandlesDeep(section, used);
  return used;
}

/**
 * Numeric max of a set of hex handle strings (non-hex / empty entries ignored).
 * Shared by the SL-6 dynamic-block minter seed and `patchHeaderHandSeedToMax`
 * so "the document's max emitted handle" has one definition.
 */
export function maxHexHandleValue(handles: Iterable<string>): number {
  let max = 0;
  for (const h of handles) {
    const n = Number.parseInt(h, 16);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/**
 * Re-patch the HEADER section's `$HANDSEED` value to `max(real object handles)
 * + 1`. Mutates the HEADER `raw-section` groups in place. No-op when there is no
 * HEADER section or no `$HANDSEED` variable in it. The new seed is never LOWERED
 * — only raised — so a source HEADER that already carried a high seed is kept.
 *
 * Codex round-2 P2 — `$HANDSEED` is itself emitted as a HEADER group-5 value, so
 * `collectAllEmittedHandles(sections)` HARVESTS the seed's own value into the max
 * (group-5 leaves are collected key-agnostically). Then `maxHandle >= currentSeed`
 * and the rewrite bumps the seed by +1 on EVERY export → the seed CHURNS up each
 * save → repeated-save diffs, breaking the byte-faithful imported-save contract.
 * The fix: EXCLUDE the `$HANDSEED` variable's own group-5 value from the
 * emitted-handle max (it is a seed, not an object handle). We temporarily
 * neutralise the seed group's code so the harvest skips it, collect the max over
 * the REAL object handles, then restore + write. The seed is left UNCHANGED when
 * it is already strictly greater than every real handle (no churn-bump).
 */
export function patchHeaderHandSeedToMax(sections: DxfSection[]): void {
  const header = sections.find(
    (s): s is Extract<DxfSection, { kind: "raw-section" }> =>
      s.kind === "raw-section" && s.name === "HEADER",
  );
  if (!header) return;
  // Locate the `9 $HANDSEED` / `5 <hex>` pair.
  let seedIndex = -1;
  for (let i = 0; i < header.groups.length - 1; i += 1) {
    if (header.groups[i].code === 9 && header.groups[i].value === "$HANDSEED") {
      seedIndex = i + 1;
      break;
    }
  }
  if (seedIndex < 0) return;
  // Exclude the seed's OWN group-5 value from the emitted-handle scan: temporarily
  // re-code it to a non-handle group so `harvestRawHandleGroup` (which harvests
  // only code 5 / 1005) skips it, collect the REAL object-handle max, then restore.
  const seedGroup = header.groups[seedIndex];
  const seedValue = seedGroup.value;
  header.groups[seedIndex] = { code: -1, value: seedValue };
  const emitted = collectAllEmittedHandles(sections);
  header.groups[seedIndex] = seedGroup;
  // T9b review round 3 (P2) — `bigint` end to end: a handle at or above 2^53
  // would round under `number` arithmetic and the seed could land AT (not
  // above) a minted handle.
  const maxHandle = maxHexHandleValueBig(emitted);
  // Respect a higher existing seed (never lower it). When the current seed already
  // strictly exceeds every real object handle the target EQUALS it → the seed is
  // written back UNCHANGED (no churn across repeated saves).
  const currentSeed = /^[0-9A-Fa-f]+$/.test(seedValue.trim())
    ? BigInt(`0x${seedValue.trim()}`)
    : 0n;
  const candidate = maxHandle + 1n;
  const target = candidate > currentSeed ? candidate : currentSeed;
  header.groups[seedIndex] = { code: 5, value: formatHexHandle(target) };
}
